import React, { useEffect, useState } from "react";
import { Lock, ShoppingBag, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/Logo";

function ProductCard({ p }) {
  const { t } = useI18n();
  const { add } = useCart();
  const [variant, setVariant] = useState(p.variants?.[0] || "");
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all flex flex-col" data-testid={`product-card-${p.id}`}>
      <div className="h-56 overflow-hidden bg-slate-100">{p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />}</div>
      <div className="p-5 flex flex-col flex-1">
        <div className="text-xs uppercase tracking-widest text-th-gold font-semibold">{p.category}</div>
        <h3 className="mt-1 font-display font-semibold text-lg text-th-blue leading-snug">{p.name}</h3>
        <p className="mt-2 text-sm text-slate-500 flex-1">{p.description}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="font-display font-bold text-2xl text-th-blue">{p.price.toFixed(2)} €</span>
          {p.variants?.length > 0 && (
            <Select value={variant} onValueChange={setVariant}>
              <SelectTrigger className="w-28 h-9" data-testid={`product-variant-${p.id}`}><SelectValue placeholder={t("choose_variant")} /></SelectTrigger>
              <SelectContent>{p.variants.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <Button onClick={() => { add(p, variant); toast.success(t("add_to_cart")); }} data-testid={`add-to-cart-${p.id}`} className="mt-4 w-full bg-th-blue text-white hover:bg-th-bluelight">
          <ShoppingBag className="w-4 h-4 mr-2" />{t("add_to_cart")}
        </Button>
      </div>
    </div>
  );
}

export default function Store() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/store/products")
      .then((r) => setProducts(r.data))
      .catch((e) => { if (e.response?.status === 403) setHidden(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-20 text-slate-500">{t("loading")}</div>;

  if (hidden) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-th-blue th-pattern px-4 py-20">
        <div className="max-w-lg text-center bg-white rounded-2xl p-10 shadow-2xl" data-testid="store-hidden-banner">
          <div className="w-16 h-16 rounded-full bg-th-blue/10 flex items-center justify-center mx-auto"><Lock className="w-7 h-7 text-th-blue" /></div>
          <h1 className="mt-5 font-display font-bold text-2xl text-th-blue">{t("store_hidden_title")}</h1>
          <p className="mt-3 text-slate-600">{t("store_hidden_text")}</p>
          <Logo size={40} className="mx-auto mt-6" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-th-blue th-pattern text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display font-bold text-4xl sm:text-5xl">{t("store_title")}</h1>
          <p className="mt-3 text-white/75 max-w-2xl text-lg">{t("store_subtitle")}</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
          {products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>
    </div>
  );
}
